import React, {useEffect, useRef, useState} from 'react';
import {Button, Input, Tooltip} from 'antd';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {Search as SearchIcon, SquareSlash} from 'lucide-react';
import './Search.scss';

const MODIFIER_KEY_LABEL = /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';

const isTypingTarget = (target) => (
    target instanceof HTMLElement
    && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
);

const URL_SEARCH_PATHS = [
    '/devices',
    '/devices/company',
    '/orders/sale',
    '/orders/refund',
    '/orders/prepaid',
    '/orders/credit',
    '/payments/logs',
    '/payments/sms',
    '/email/list',
    '/products',
    '/s2p/payme/logs',
    '/s2p/click/logs',
    '/s2p/uzum/logs',
];

const SearchComponent = ({t, searchText, setSearchText, fieldClassName = '', buttonClassName = ''}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const searchInputRef = useRef(null);
    const [inputValue, setInputValue] = useState(searchText);
    const isUrlSearchPage = URL_SEARCH_PATHS.includes(location.pathname);

    useEffect(() => {
        if (isUrlSearchPage) {
            const urlSearchText = searchParams.get('search') || '';
            setSearchText(urlSearchText);
            setInputValue(urlSearchText);
            return;
        }

        setSearchText('');
        setInputValue('');
    }, [isUrlSearchPage, location.pathname, location.search, searchParams, setSearchText]);

    useEffect(() => {
        setInputValue(searchText);
    }, [searchText]);

    // Trigger only when Enter/search button is pressed
    const handleSearch = (value) => {
        const nextSearchText = value.trim();
        setSearchText(nextSearchText);
        setInputValue(nextSearchText);

        if (isUrlSearchPage) {
            const nextParams = new URLSearchParams(searchParams);

            if (nextSearchText) {
                nextParams.set('search', nextSearchText);
            } else {
                nextParams.delete('search');
            }

            nextParams.set('page', '1');
            navigate({
                pathname: location.pathname,
                search: nextParams.toString(),
            }, {replace: true});
        }

        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    useEffect(() => {
        const handler = (e) => {
            if (e.key !== '/') {
                return;
            }

            const withModifier = e.metaKey || e.ctrlKey;

            // Plain "/" must still type into other fields; Cmd/Ctrl + / focuses search from anywhere
            if (!withModifier && isTypingTarget(e.target)) {
                return;
            }

            e.preventDefault();

            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        };

        window.addEventListener('keydown', handler);

        return () => {
            window.removeEventListener('keydown', handler);
        };
    }, []);

    return (
        <div className="search">
            <div className={`search__field ${fieldClassName}`}>
                <Input
                    className="search-input-field"
                    ref={searchInputRef}
                    prefix={
                        <Tooltip title={`Press / or ${MODIFIER_KEY_LABEL} + / to activate search input`}>
                            <SquareSlash
                                size={14}
                                style={{
                                    color: 'rgba(0,0,0,.45)',
                                }}
                            />
                        </Tooltip>
                    }
                    allowClear
                    showCount
                    placeholder={t('header.placeholder1')}
                    value={inputValue}
                    onChange={(event) => setInputValue(event.target.value)}
                    onPressEnter={() => handleSearch(inputValue)}
                    onClear={() => handleSearch('')}
                    style={{width: '100%'}}
                />
            </div>
            <Button
                type="text"
                className={`search__button ${buttonClassName}`}
                aria-label={t('header.placeholder1')}
                icon={<SearchIcon size={18}/>}
                onClick={() => handleSearch(inputValue)}
            />
        </div>
    );
};

export default SearchComponent;
